import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import {
  CheckCircle,
  AlertCircle,
  Info,
  Cable,
  Upload,
  File,
  Database,
  Check,
} from "lucide-react";
import { Button } from "@shared/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";

interface UtilitySetupCardProps {
  progress: number;
  orgSetupData: any;
}

export function UtilitySetupCard({
  progress,
  orgSetupData,
}: UtilitySetupCardProps) {
  const navigate = useNavigate();
  const setupSteps = [
    {
      title: "System Settings & Service Coverage",
      status:
        orgSetupData?.overallProgressParameters === 100
          ? "completed"
          : "pending",
      details: ["Core System Settings", "Service Areas"],
    },
    {
      title: "Billing & Scheduling Management",
      status:
        orgSetupData?.overallProgressParameters === 100
          ? "completed"
          : "pending",
      details: ["Plans & Tariffs", "Calendar & Scheduling"],
    },
    {
      title: "Access Control & References",
      status:
        orgSetupData?.overallProgressParameter === 100
          ? "completed"
          : "pending",
      details: ["Staff & Access Control", "ID & Reference Numbers"],
    },
  ];

  return (
    <TooltipProvider>
      <Card className="flex flex-col h-full">
        <CardHeader className="flex flex-row items-center pb-2">
          <CardTitle className="text-md font-medium flex items-center">
            <Cable className="w-4 h-4 mr-2" />
            Utility Setup
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col flex-1">
          <div className="space-y-4 flex-1">
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Setup progress</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className="space-y-2">
              {setupSteps.map((step, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  {step.status === "completed" ? (
                    <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                  )}
                  <span className="flex-1">{step.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {/* ({step.weight}) */}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-48">
                      <div className="space-y-1">
                        <p className="font-medium text-sm">{step.title}</p>
                        <p className="text-xs text-muted-foreground">
                          Combines:
                        </p>
                        <ul className="text-xs space-y-0.5">
                          {step.details.map((detail, detailIndex) => (
                            <li key={detailIndex}>• {detail}</li>
                          ))}
                        </ul>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              ))}
            </div>
          </div>

          <Button
            className="w-full mt-4"
            onClick={() => navigate("/utility/utility-setup/utility-admin-dashboard")}
          >
            Continue Setup
          </Button>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

export function DataUploadCard() {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center pb-2">
        <CardTitle className="text-md font-medium flex items-center">
          <Upload className="w-4 h-4 mr-2" /> Data Upload
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <p className="text-xs text-muted-foreground mb-2 text-center">
              Simplify your data integration process with our smart tools
            </p>
            <p className="text-xs text-muted-foreground mb-4 text-center">
              Automated recognition, mapping, and validation ensure accuracy
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-8 h-8 mx-auto flex items-center justify-center mb-1">
                <File className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-xs font-medium">Smart Recognition</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-8 h-8 mx-auto flex items-center justify-center mb-1">
                <Database className="w-4 h-4 text-purple-600" />
              </div>
              <p className="text-xs font-medium">Data Mapping</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 rounded-full w-8 h-8 mx-auto flex items-center justify-center mb-1">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <p className="text-xs font-medium">Smart Validation</p>
            </div>
          </div>

          <Button
            className="w-full"
            onClick={() => navigate("/onboarding/data-migration/data-cards")}
          >
            Go to Data Upload
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Helper component for the Utility icon
function UtilityIcon(props: React.ComponentProps<"svg">) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
      <path d="M19 3v4" />
      <path d="M21 5h-4" />
    </svg>
  );
}
